import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { Weight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { useEffect, useRef, useState } from "react";
import {
  buildLabelPurchasePinChecks,
  type ModalOptions,
  type PinVerificationConfig,
} from "./pin-verification-context";

export type FulfillmentLabelAction =
  | FulfillmentGroupActions.CREATE_AND_PRINT_LABEL
  | FulfillmentGroupActions.CREATE_LABEL
  | FulfillmentGroupActions.PRINT_LABEL;

export function isFulfillmentLabelAction(
  action: FulfillmentGroupActions,
): action is FulfillmentLabelAction {
  return (
    action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL ||
    action === FulfillmentGroupActions.CREATE_LABEL ||
    action === FulfillmentGroupActions.PRINT_LABEL
  );
}

export interface UseFulfillmentLabelActionControllerParams {
  blockedReason?: string;
  executeWithAnyPinCheck(
    action: () => void,
    checks: { config: PinVerificationConfig; modalOptions?: ModalOptions }[],
  ): void;
  inFlightLock?: FulfillmentLabelActionInFlightLock;
  isButtonDisabledByUpsAgreement: boolean;
  onBlocked(reason: string): void;
  prerequisitesPending: boolean;
  purchase(
    action:
      | FulfillmentGroupActions.CREATE_AND_PRINT_LABEL
      | FulfillmentGroupActions.CREATE_LABEL,
    signal: AbortSignal,
  ): Promise<void>;
  reprint(): Promise<void> | void;
  totalRatePrice: number | null;
  totalWeight: Weight | null;
}

export interface FulfillmentLabelActionInFlightLock {
  current: boolean;
}

export function useFulfillmentLabelActionController({
  blockedReason,
  executeWithAnyPinCheck,
  inFlightLock,
  isButtonDisabledByUpsAgreement,
  onBlocked,
  prerequisitesPending,
  purchase,
  reprint,
  totalRatePrice,
  totalWeight,
}: UseFulfillmentLabelActionControllerParams): {
  execute(action: FulfillmentLabelAction): void;
  pending: boolean;
} {
  const localInFlightRef = useRef(false);
  const inFlightRef = inFlightLock ?? localInFlightRef;
  const abortControllerRef = useRef<AbortController | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const runExclusive = useHandler(
    async (action: (signal: AbortSignal) => Promise<void> | void) => {
      if (inFlightRef.current) {
        return;
      }

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      inFlightRef.current = true;
      setPending(true);
      try {
        await action(abortController.signal);
      } finally {
        inFlightRef.current = false;
        setPending(false);
      }
    },
  );

  const execute = useHandler((action: FulfillmentLabelAction) => {
    if (prerequisitesPending || inFlightRef.current) {
      return;
    }
    if (blockedReason) {
      onBlocked(blockedReason);
      return;
    }

    if (action === FulfillmentGroupActions.PRINT_LABEL) {
      // PrintBatchProvider owns reprint confirmation because it knows whether
      // the requested labels have already been printed.
      void runExclusive(() => reprint());
      return;
    }

    if (isButtonDisabledByUpsAgreement) {
      onBlocked("Accept the UPS agreement before printing the label");
      return;
    }

    executeWithAnyPinCheck(
      () => {
        void runExclusive((signal) => purchase(action, signal));
      },
      buildLabelPurchasePinChecks(totalWeight, totalRatePrice),
    );
  });

  return { execute, pending };
}
